import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, Button, Switch, Space, Tabs, FormInstance } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { RadioCardGroup } from '../frontdesk/components/RadioCardGroup';
import { MediaUploadField } from './MediaUploadField';
import { RoomType, RoomImage } from './Rooms';

interface RoomTypeModalProps {
  open: boolean;
  onCancel: () => void;
  form: FormInstance;
  editingRoomType: RoomType | null;
  onSubmit: (values: any) => void;
  loading: boolean;
  canManage: boolean;
  handleRoomContentUpload: (fieldName: 'hero_image_url' | 'overview_image_url' | 'detail_image_url', file: File, mediaType: 'image' | 'media') => Promise<void>;
  handleRoomGalleryUpload: (index: number, file: File) => Promise<void>;
}

export const RoomTypeModal: React.FC<RoomTypeModalProps> = ({
  open,
  onCancel,
  form,
  editingRoomType,
  onSubmit,
  loading,
  canManage,
  handleRoomContentUpload,
  handleRoomGalleryUpload,
}) => {
  const [activeTab, setActiveTab] = useState("1");
  const acStatusPref = Form.useWatch('ac_status_preference', form);

  const validateTab = async (tab: string) => {
    if (tab === "1") {
      await form.validateFields([
        "name",
        "slug",
        "description",
        "base_price",
        "ac_status_preference",
        "ac_base_price",
        "non_ac_base_price",
        "extra_bed_price",
        "max_occupancy",
        "size_sqm",
        "bed_type",
        "view_type",
        "amenities",
        "highlight_points"
      ]);
    } else if (tab === "2") {
      await form.validateFields([
        "hero_title",
        "hero_subtitle",
        "hero_image_url",
        "overview_kicker",
        "overview_title",
        "overview_description",
        "overview_image_url",
        "detail_kicker",
        "detail_title",
        "detail_description",
        "detail_image_url",
        "booking_panel_title",
        "is_featured"
      ]);
    }
  };

  const handleNext = async () => {
    try {
      await validateTab(activeTab);
      setActiveTab(String(Number(activeTab) + 1));
    } catch (e) {
      // Validation failed, errors will be shown inline by Ant Design Form
    }
  };

  return (
    <Modal
      title={editingRoomType ? 'Edit Room Type' : 'Add Room Type'}
      open={open}
      onCancel={() => {
        onCancel();
        setActiveTab("1");
      }}
      destroyOnClose
      width={760}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button onClick={() => { form.resetFields(); setActiveTab("1"); }}>Reset</Button>
          <Space>
            {activeTab !== "1" && (
              <Button onClick={() => setActiveTab(String(Number(activeTab) - 1))}>Back</Button>
            )}
            {activeTab !== "3" && (
              <Button id="room-type-next-btn" type="primary" onClick={handleNext}>Next</Button>
            )}
            {activeTab === "3" && (
              <Button id="room-type-submit-btn" type="primary" loading={loading} onClick={() => form.submit()}>
                {editingRoomType ? 'Save Changes' : 'Create Room Type'}
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Tabs
          activeKey={activeTab}
          onTabClick={async (key) => {
            if (key !== activeTab) {
              try {
                // Validate current tab before moving away
                await validateTab(activeTab);
                
                setActiveTab(key);
              } catch (e) {
                // Validation failed, do not change tab
              }
            }
          }}
          items={[
            {
              key: "1",
              label: "1. Basic Info",
              children: (
                <div>
                  <Form.Item
                    label="Room Type Name"
                    name="name"
                    rules={[{ required: true, message: 'Room type name is required' }]}
                  >
                    <Input placeholder="e.g., Deluxe Suite" />
                  </Form.Item>

                  <Form.Item
                    label="Slug"
                    name="slug"
                    tooltip="Used by frontend room details page, e.g. cloud-9-living"
                  >
                    <Input placeholder="e.g., cloud-9-living" />
                  </Form.Item>

                  <Form.Item
                    label="Description"
                    name="description"
                  >
                    <Input.TextArea rows={3} placeholder="Room description" />
                  </Form.Item>

                  <Form.Item
                    label="AC Availability"
                    name="ac_status_preference"
                    rules={[{ required: true, message: 'Please select AC availability' }]}
                    initialValue="both"
                  >
                    <RadioCardGroup
                      options={[
                        { label: 'AC Only', value: 'ac' },
                        { label: 'Non-AC Only', value: 'non_ac' },
                        { label: 'Both AC & Non-AC', value: 'both' }
                      ]}
                    />
                  </Form.Item>

                  <div className="grid-two">
                    {acStatusPref === 'both' ? (
                      <>
                        <Form.Item
                          label="AC Room Base Price (per Night)"
                          name="ac_base_price"
                          rules={[{ required: true, message: 'Price is required' }]}
                        >
                          <InputNumber prefix="LKR" style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                          label="Non-AC Room Base Price (per Night)"
                          name="non_ac_base_price"
                          rules={[{ required: true, message: 'Price is required' }]}
                        >
                          <InputNumber prefix="LKR" style={{ width: '100%' }} />
                        </Form.Item>
                        {/* Hidden base price field to satisfy older logic, defaults to non_ac price if both */}
                        <Form.Item name="base_price" hidden initialValue={0}>
                          <InputNumber />
                        </Form.Item>
                      </>
                    ) : (
                      <Form.Item
                        label="Base Price per Night"
                        name="base_price"
                        rules={[{ required: true, message: 'Price is required' }]}
                      >
                        <InputNumber prefix="LKR" style={{ width: '100%' }} />
                      </Form.Item>
                    )}

                    <Form.Item
                      label="Extra Bed Price per Night"
                      name="extra_bed_price"
                      initialValue={0}
                      rules={[{ required: true, message: 'Extra bed price is required' }]}
                    >
                      <InputNumber prefix="LKR" min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      label="Max Occupancy"
                      name="max_occupancy"
                      rules={[{ required: true, message: 'Max occupancy is required' }]}
                    >
                      <InputNumber min={1} max={8} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      label="Room Size (sqm)"
                      name="size_sqm"
                    >
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      label="Bed Type"
                      name="bed_type"
                      style={{ gridColumn: '1 / -1' }}
                    >
                      <RadioCardGroup
                        options={[
                          { label: 'Single', value: 'Single' },
                          { label: 'Double', value: 'Double' },
                          { label: 'Twin', value: 'Twin' },
                          { label: 'Queen', value: 'Queen' },
                          { label: 'King', value: 'King' },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item
                      label="View Type"
                      name="view_type"
                      style={{ gridColumn: '1 / -1' }}
                    >
                      <RadioCardGroup
                        options={[
                          { label: 'Ocean View', value: 'Ocean View' },
                          { label: 'Garden View', value: 'Garden View' },
                          { label: 'Pool View', value: 'Pool View' },
                          { label: 'City View', value: 'City View' },
                          { label: 'No View', value: 'No View' },
                        ]}
                      />
                    </Form.Item>
                  </div>

                  <Form.Item
                    label="Amenities (comma-separated)"
                    name="amenities"
                  >
                    <Input placeholder="e.g., Air Conditioning, WiFi, TV, Mini Bar" />
                  </Form.Item>

                  <Form.Item
                    label="Highlights (comma-separated)"
                    name="highlight_points"
                  >
                    <Input placeholder="e.g., Private infinity pool, 360 sky view" />
                  </Form.Item>
                </div>
              )
            },
            {
              key: "2",
              label: "2. Page Content",
              children: (
                <div className="grid-two">
                  <Form.Item label="Hero Title" name="hero_title">
                    <Input placeholder="Luxurious Cloud 9 Living" />
                  </Form.Item>
                  <Form.Item label="Hero Subtitle" name="hero_subtitle">
                    <Input placeholder="Spacious sanctuary villa..." />
                  </Form.Item>
                  <div style={{ gridColumn: '1 / span 2' }}>
                    <MediaUploadField
                      form={form}
                      fieldName="hero_image_url"
                      label="Hero Image"
                      accept="image/*"
                      disabled={!canManage}
                      onUpload={(file) => handleRoomContentUpload('hero_image_url', file, 'image')}
                    />
                  </div>

                  <Form.Item label="Overview Kicker" name="overview_kicker">
                    <Input placeholder="PRISTINE NATURE, MODERN LUXURY" />
                  </Form.Item>
                  <Form.Item label="Overview Title" name="overview_title">
                    <Input placeholder="Greetings from Boundless Tranquility" />
                  </Form.Item>
                  <Form.Item label="Overview Description" name="overview_description" style={{ gridColumn: '1 / span 2' }}>
                    <Input.TextArea rows={3} />
                  </Form.Item>
                  <div style={{ gridColumn: '1 / span 2' }}>
                    <MediaUploadField
                      form={form}
                      fieldName="overview_image_url"
                      label="Overview Media (Image or Video)"
                      accept="image/*,video/*"
                      disabled={!canManage}
                      onUpload={(file) => handleRoomContentUpload('overview_image_url', file, 'media')}
                    />
                  </div>

                  <Form.Item label="Detail Kicker" name="detail_kicker">
                    <Input placeholder="DELUXE ROOM" />
                  </Form.Item>
                  <Form.Item label="Detail Title" name="detail_title">
                    <Input placeholder="Thoughtfully Curated For Deep Comfort" />
                  </Form.Item>
                  <Form.Item label="Detail Description" name="detail_description" style={{ gridColumn: '1 / span 2' }}>
                    <Input.TextArea rows={3} />
                  </Form.Item>
                  <div style={{ gridColumn: '1 / span 2' }}>
                    <MediaUploadField
                      form={form}
                      fieldName="detail_image_url"
                      label="Detail Image"
                      accept="image/*"
                      disabled={!canManage}
                      onUpload={(file) => handleRoomContentUpload('detail_image_url', file, 'image')}
                    />
                  </div>

                  <Form.Item label="Booking Panel Title" name="booking_panel_title">
                    <Input placeholder="Plan Your Stay" />
                  </Form.Item>
                  <Form.Item label="Featured Room Type" name="is_featured" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </div>
              )
            },
            {
              key: "3",
              label: "3. Gallery",
              children: (
                <div>
                  <Form.List name="images">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field) => (
                          <div
                            key={field.key}
                            style={{
                              marginBottom: 12,
                              border: '1px solid #f0f0f0',
                              borderRadius: 8,
                              padding: 12,
                            }}
                          >
                            <MediaUploadField
                              form={form}
                              fieldName={[field.name, 'image_url']}
                              absoluteFieldName={['images', field.name, 'image_url']}
                              label="Gallery Image"
                              accept="image/*"
                              disabled={!canManage}
                              onUpload={(file) => handleRoomGalleryUpload(field.name, file)}
                            />

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px auto', gap: 12 }}>
                              <Form.Item {...field} label="Alt Text" name={[field.name, 'alt_text']}>
                                <Input placeholder="Describe image" />
                              </Form.Item>
                              <Form.Item {...field} label="Order" name={[field.name, 'display_order']}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                              </Form.Item>
                              <Form.Item
                                {...field}
                                label="Primary"
                                name={[field.name, 'is_primary']}
                                valuePropName="checked"
                              >
                                <Switch />
                              </Form.Item>
                              <div style={{ display: 'flex', alignItems: 'end' }}>
                                <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)}>
                                  Remove
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}

                        <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ is_primary: false })} block>
                          Add Gallery Image
                        </Button>
                      </>
                    )}
                  </Form.List>
                </div>
              )
            }
          ]}
        />
      </Form>
    </Modal>
  );
};
